import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SuggestionModal } from "../components/SuggestionModal";
import { ProtonWidget } from "../components/ProtonWidget";
import {
  applyWindowGlobals,
  ensurePageCss,
  readCookie,
  readPageData,
  slugFromTitle,
  syncHeadMeta,
} from "../lib/bootConfig";
import { initFavoriteRadioPayload } from "../lib/favoriteRadioPayload";
import { initListeningHeartbeat } from "../lib/listeningHeartbeat";
import { initAppTooltip } from "../lib/appTooltip";
import { initRadioSidePanel } from "../lib/radioSidePanel";
import { initRadioDetailPage } from "../lib/radioDetailPage";
import { initProtonWidget } from "../lib/protonWidget";
import { RADIOS_ORIGIN } from "../lib/origins";

type Stream = { title?: string; url: string };
type Contact = { type?: string; title?: string; detail?: string; value?: string };
type PlatformInfo = { available?: boolean; store_url?: string; storeUrl?: string };

export type RadioDetail = {
  id: number;
  title?: string;
  slogan?: string;
  description?: string;
  localizacao?: string;
  segmento?: string;
  frequency?: string;
  color?: string;
  url_logo?: string;
  streams?: Stream[];
  stream_url?: string;
  contatos?: Contact[];
  platforms?: Record<string, PlatformInfo>;
  prog_title?: string;
  prog_detail?: string;
  _enrichment?: {
    platforms?: Record<string, PlatformInfo>;
    broadcasting?: { current_program?: { title?: string; detail?: string } };
  };
  [key: string]: unknown;
};

const CONTACT_ICONS: Record<string, string> = {
  phone: "fas fa-phone",
  whatsapp: "fab fa-whatsapp",
  email: "fas fa-envelope",
  facebook: "fab fa-facebook",
  instagram: "fab fa-instagram",
  threads: "fab fa-threads",
  twitter: "fab fa-x-twitter",
  telegram: "fab fa-telegram",
  youtube: "fab fa-youtube",
  tiktok: "fab fa-tiktok",
  website: "fas fa-globe",
};

const PLATFORM_DEFS: Record<string, { label: string; icon: string }> = {
  android: { label: "Android", icon: "fab fa-google-play" },
  ios: { label: "iOS", icon: "fab fa-apple" },
  alexa: { label: "Alexa", icon: "/assets/icons/alexa.svg" },
  firestick_tv: { label: "Firestick TV", icon: "fab fa-amazon" },
  roku: { label: "Roku", icon: "/assets/icons/roku.svg" },
  samsung_tv: { label: "Samsung TV", icon: "/assets/icons/samsung.svg" },
  lg_tv: { label: "LG TV", icon: "/assets/icons/lg.svg" },
  carplay: { label: "CarPlay", icon: "fas fa-car" },
  webpage: { label: "Website", icon: "fas fa-globe" },
};

function parseIdSlug(idSlug: string | undefined): { id: number; slug: string } {
  const raw = String(idSlug || "");
  const m = raw.match(/^(\d+)(?:-(.*))?$/);
  if (m) return { id: Number(m[1]), slug: m[2] || "" };
  return { id: 0, slug: raw };
}

function contactHref(contato: Contact): { href: string; target: string } {
  const type = contato.type || "";
  const value = (contato.value || "").trim();
  if (!value) return { href: "", target: "" };
  if (type === "whatsapp") {
    const digits = value.replace(/\D+/g, "");
    return digits ? { href: "https://wa.me/" + digits, target: "_blank" } : { href: "", target: "" };
  }
  if (type === "phone") return { href: "tel:" + value, target: "" };
  if (type === "youtube") {
    if (/^https?:\/\//i.test(value)) return { href: value, target: "_blank" };
    if (/^UC[\w-]{22}$/.test(value)) return { href: "https://www.youtube.com/channel/" + value, target: "_blank" };
    return { href: "https://www.youtube.com/@" + value.replace(/^@/, ""), target: "_blank" };
  }
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return { href: "mailto:" + value, target: "" };
  if (/^https?:\/\//i.test(value)) return { href: value, target: "_blank" };
  return { href: "", target: "" };
}

const RADIOSNET_AD_NOTICE =
  "attention: this station uses a streaming service that inserts advertising in the beginning and/or during the broadcast, causing interruptions in the audio stream. these ads are of the station's responsibility and finance their running costs. in the face of this fact, we inform that audio advertising are not of radiosnet's responsibility.";

function getVisibleDescription(raw: unknown): string {
  const text = String(raw || "").trim();
  if (!text) return "";
  const norm = text
    .replace(/^\*+\s*/, "")
    .replace(/\s*\*+$/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return norm === RADIOSNET_AD_NOTICE ? "" : text;
}

function buildRadioSeoSummary(radio: RadioDetail | null, hasStream: boolean): string {
  if (!radio || !hasStream) return "";
  const title = String(radio.title || "").trim().replace(/\s+/g, " ");
  if (!title) return "";
  const details: string[] = [];
  const seg = String(radio.segmento || "").trim().replace(/\s+/g, " ");
  const loc = String(radio.localizacao || "").trim().replace(/\s+/g, " ");
  const freq = String(radio.frequency || "").trim().replace(/\s+/g, " ");
  if (seg) details.push(`o estilo ${seg}`);
  if (loc) details.push(`a localização ${loc}`);
  if (freq) details.push(`a frequência ${freq}`);
  const context = details.length
    ? `O cadastro informa ${details.join(", ")}.`
    : "Consulte os streams cadastrados e os dados de programação disponíveis da emissora.";
  return `Ouça ${title} ao vivo no Radios.PRO. ${context} Escolha um stream para ouvir; se o áudio não iniciar, tente outra transmissão.`;
}

function buildRadioMetaDescription(radio: RadioDetail): string {
  const title = String(radio.title || "").trim();
  const seg = String(radio.segmento || "").trim();
  const loc = String(radio.localizacao || "").trim();
  const visible = getVisibleDescription(radio.description).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const slogan = String(radio.slogan || "").trim();
  let share = visible || slogan;
  if (share) {
    if (loc && !share.toLowerCase().includes(loc.toLowerCase())) {
      share += ` (${loc})`;
    }
    if (!share.toLowerCase().includes("ouça") && !share.toLowerCase().includes("radios.pro")) {
      share += " Ouça ao vivo no Radios.pro.";
    }
  } else if (seg && title) {
    share = `Ouça ${title} ao vivo — ${seg}${loc ? ` (${loc})` : ""}. Streaming no Radios.pro.`;
  } else if (title) {
    share = `Ouça ${title} ao vivo online${loc ? ` (${loc}).` : "."} Streaming no Radios.pro.`;
  } else {
    share = seg || "Rádio online no Radios.pro.";
  }
  return share.length > 300 ? share.slice(0, 297) + "..." : share;
}

function dismissRadioSplash() {
  const splash = document.getElementById("rp-splash");
  document.body.classList.remove("rp-loading");
  if (!splash) return;
  splash.classList.add("rp-splash--done");
}

function readInitialRadio(id: number): RadioDetail | null {
  if (!id) return null;
  const data = readPageData<{ page?: string; radio?: RadioDetail | null }>();
  if (data && data.page === "radio" && data.radio && Number(data.radio.id) === id) {
    return data.radio;
  }
  return null;
}

export function RadioPage() {
  const { idSlug } = useParams();
  const { id } = parseIdSlug(idSlug);
  const [radio, setRadio] = useState<RadioDetail | null>(() => readInitialRadio(id));
  const [status, setStatus] = useState<"loading" | "ok" | "missing">(() =>
    readInitialRadio(id) ? "ok" : "loading",
  );
  const loggedIn = typeof document !== "undefined" && readCookie("RPRO_USER_UID") !== "";

  useEffect(() => {
    document.body.className = "rp-body rp-loading";
    ensurePageCss("/assets/css/radio-side-panel.css");
    ensurePageCss("/assets/css/radio-detail-extras.css");
    if (!document.querySelector("script[data-rp-tailwind]")) {
      const s = document.createElement("script");
      s.src = "https://cdn.tailwindcss.com";
      s.dataset.rpTailwind = "1";
      document.head.appendChild(s);
    }
    const t = setTimeout(dismissRadioSplash, 8000);
    return () => {
      clearTimeout(t);
      const audio = document.getElementById("radio-detail-audio") as HTMLAudioElement | null;
      if (audio) {
        try {
          audio.pause();
          audio.removeAttribute("src");
        } catch {}
      }
      document.body.classList.remove("has-radio-global-player", "radio-report-modal-open");
    };
  }, [id]);

  useEffect(() => {
    if (!id) {
      setStatus("missing");
      return;
    }
    const preloaded = readInitialRadio(id);
    if (preloaded) {
      setRadio(preloaded);
      setStatus("ok");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    fetch(`/ajax/radio-search.php?action=get-single&id=${id}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const item = d.radio || d;
        if (item && item.id) {
          setRadio(item);
          setStatus("ok");
        } else {
          setRadio(null);
          setStatus("missing");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRadio(null);
          setStatus("missing");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (status === "loading") return;
    requestAnimationFrame(() => requestAnimationFrame(dismissRadioSplash));
  }, [status, radio]);

  useEffect(() => {
    if (status === "missing") {
      syncHeadMeta({
        title: "Rádio não encontrada - Radios.pro",
        description: "Não foi possível carregar as informações desta rádio. Verifique o link ou tente novamente mais tarde.",
        canonical: null,
        robots: "noindex, nofollow",
        ogTitle: null,
      });
      return;
    }
    if (status !== "ok" || !radio) return;
    const title = String(radio.title || "").trim() || "Rádio";
    const stationSlug = slugFromTitle(title);
    const canonicalPath = `/radio/${radio.id}${stationSlug ? "-" + stationSlug : ""}`;
    if (window.location.pathname.replace(/\/+$/, "") !== canonicalPath) {
      window.history.replaceState(null, "", canonicalPath + window.location.search + window.location.hash);
    }
    const canonicalUrl = `${RADIOS_ORIGIN}${canonicalPath}`;
    const desc = buildRadioMetaDescription(radio);
    syncHeadMeta({
      title: `${title} - Radios.pro`,
      description: desc,
      canonical: canonicalUrl,
      robots: null,
      ogTitle: title,
      ogDescription: desc,
      ogUrl: canonicalUrl,
      ogImage: `${RADIOS_ORIGIN}/og/radio/${radio.id}.jpg?v=2`,
      ogSiteName: "Radios.pro",
    });
    window.__RADIOPRO_PAGE__ = { id: radio.id, title };
    applyWindowGlobals();
    initFavoriteRadioPayload();
    initRadioDetailPage();
    initRadioSidePanel();
    initAppTooltip();
    initListeningHeartbeat();
    initProtonWidget();
  }, [status, radio]);

  const logo = radio?.url_logo || "/assets/img/header/logo.png";
  const color = radio?.color && /^#/.test(radio.color) ? radio.color : "#111";
  const streams: Stream[] = useMemo(() => {
    if (!radio) return [];
    if (Array.isArray(radio.streams) && radio.streams.length) {
      return radio.streams.filter((s) => s && s.url);
    }
    if (radio.stream_url) return [{ title: "Stream", url: radio.stream_url }];
    return [];
  }, [radio]);
  const defaultStream = streams[0];
  const contacts = Array.isArray(radio?.contatos) ? radio!.contatos : [];
  const platformsData = radio?.platforms || radio?._enrichment?.platforms || {};
  const platformEntries = Object.entries(platformsData).map(([key, info]) => {
    const def = PLATFORM_DEFS[key] || { label: key.replace(/_/g, " "), icon: "fas fa-circle" };
    const available = !!(info && (info.available || info.store_url || info.storeUrl));
    return {
      key,
      label: def.label,
      icon: def.icon,
      available,
      store_url: (info && (info.store_url || info.storeUrl)) || "",
    };
  });
  const programTitle =
    radio?.prog_title ||
    radio?._enrichment?.broadcasting?.current_program?.title ||
    "Programação ao vivo";
  const programDetail =
    radio?.prog_detail ||
    radio?._enrichment?.broadcasting?.current_program?.detail ||
    (radio?.segmento ? String(radio.segmento) : "Acompanhe a transmissão da emissora.");
  const slug = slugFromTitle(radio?.title || "") || String(id);
  const landingHref = `/radio/app-landing/${id}${slug ? "-" + slug : ""}`;
  const visibleDesc = getVisibleDescription(radio?.description);
  const seoSummary = buildRadioSeoSummary(radio, streams.length > 0);

  return (
    <>
      <div id="rp-splash" className="rp-splash" role="status" aria-label="Carregando Radios.PRO">
        <div className="rp-splash__inner">
          <div className="rp-splash__logo-wrap" aria-hidden="true">
            <span className="rp-splash__ring"></span>
            <img className="rp-splash__logo" src="/assets/img/header/logo.png" alt="" width={88} height={88} />
          </div>
        </div>
      </div>

      <Header />
      <SuggestionModal />

      <main className="radio-detail-page">
        <aside id="rp-side-panel" className="rp-side-panel" aria-label="Recursos da rádio" hidden>
          <div className="rp-side-panel__inner" id="rp-side-panel-inner">
            <div className="rp-side-panel__top">
              <span className="rp-side-panel__brand" aria-hidden="true">
                <i className="fas fa-compass"></i>
              </span>
              <span className="rp-side-panel__title">Nesta rádio</span>
              <button
                type="button"
                className="rp-side-panel__pin"
                id="rp-side-panel-pin"
                aria-pressed="false"
                aria-label="Fixar painel aberto"
                title="Fixar painel"
              >
                <i className="fas fa-thumbtack" aria-hidden="true"></i>
              </button>
            </div>
            <nav className="rp-side-panel__nav" id="rp-side-panel-nav" aria-label="Seções da página"></nav>
          </div>
        </aside>
        <button
          type="button"
          id="rp-side-panel-fab"
          className="rp-side-panel-fab"
          aria-label="Abrir recursos da rádio"
          aria-expanded="false"
          hidden
        >
          <i className="fas fa-chevron-down" aria-hidden="true"></i>
        </button>

        <section className="radio-detail-hero" data-rp-section="info" data-rp-label="Informações" data-rp-icon="info">
          {status === "missing" && (
            <section className="radio-detail-error">
              <h1>Rádio não encontrada</h1>
              <p>Não foi possível carregar as informações desta rádio. Verifique o link ou tente novamente mais tarde.</p>
              <a href="/" className="btn-primary">
                Voltar para a página inicial
              </a>
            </section>
          )}

          {radio && (
            <>
              <div className="radio-detail-hero-top rd-hero-v2">
                <div className="rd-row rd-row-identity">
                  <div className="rd-card rd-col-identity">
                    <div className="rd-identity-flex">
                      <div
                        className="rd-logo-wrap radio-detail-logo-wrapper"
                        style={{
                          backgroundColor: color,
                          backgroundImage: `url('/assets/img/header/logo.png')`,
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                        }}
                      >
                        <img
                          src={logo}
                          alt={radio.title || "Rádio"}
                          className="radio-detail-logo"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src = "/assets/img/header/logo.png";
                          }}
                        />
                      </div>
                      <div className="rd-identity-info">
                        <h1 className="radio-detail-title rd-title">
                          <span className="rd-title-text">{radio.title}</span>
                          <a
                            className="radio-app-landing-link"
                            href={landingHref}
                            title="Página web app da emissora"
                            aria-label="Abrir landing web app desta rádio"
                          >
                            <i className="fas fa-mobile-screen-button" aria-hidden="true"></i>
                          </a>
                        </h1>
                        {radio.slogan ? <p className="radio-detail-slogan-inline rd-slogan">{radio.slogan}</p> : null}
                        {radio.localizacao ? (
                          <p className="radio-detail-location rd-meta-line" data-tooltip="Localização declarada pela rádio.">
                            {radio.localizacao}
                          </p>
                        ) : null}
                        {radio.frequency ? (
                          <p
                            className="radio-detail-frequency rd-meta-line"
                            data-tooltip="Frequência de transmissão (FM/AM) da rádio."
                          >
                            {radio.frequency}
                          </p>
                        ) : null}
                        {radio.segmento ? (
                          <p
                            className="radio-detail-segment rd-meta-line"
                            data-tooltip="Segmento musical ou temático principal da rádio."
                          >
                            {radio.segmento}
                          </p>
                        ) : null}
                        {visibleDesc || seoSummary ? (
                          <div className="rd-description-inline">
                            {visibleDesc ? <p>{visibleDesc}</p> : null}
                            {seoSummary ? (
                              <>
                                <h2>Como ouvir esta rádio</h2>
                                <p>{seoSummary}</p>
                                <a className="rd-seo-guide-link" href="/como-escutar-radio-online">
                                  Veja como escutar rádio online ao vivo
                                </a>
                              </>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="rd-card rd-col-program">
                    <div
                      className="radio-detail-program rd-program"
                      id="radio-detail-program-current"
                      data-fallback-title={programTitle}
                      data-fallback-detail={programDetail}
                      data-placeholder-title="Programação ao vivo"
                      data-placeholder-detail="Acompanhe a transmissão da emissora."
                      data-no-current-title="Fora da grade"
                      data-no-current-detail="Nenhum programa listado para este horário."
                    >
                      <div className="radio-detail-live-label">
                        <span className="radio-detail-live-dot" aria-hidden="true"></span>
                        <span className="radio-detail-live-text">No ar agora</span>
                      </div>
                      <p className="radio-detail-program-title" data-program-title>
                        {programTitle}
                      </p>
                      <p className="radio-detail-program-detail" data-program-detail>
                        {programDetail}
                      </p>
                    </div>

                    {defaultStream && (
                      <div className="radio-detail-streams rd-player">
                        <h2 className="rd-player-title">Streams disponíveis</h2>
                        <audio id="radio-detail-audio" className="radio-detail-audio-player" preload="none"></audio>
                        <div className="rd-player-controls">
                          <button
                            type="button"
                            className="radio-detail-stream-play rd-player-play"
                            data-stream-url={defaultStream.url}
                            aria-label="Reproduzir stream"
                          >
                            <span className="radio-detail-stream-play-icon">
                              <i className="fas fa-play" aria-hidden="true"></i>
                            </span>
                            <span className="radio-detail-stream-play-loading" aria-hidden="true">
                              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                            </span>
                          </button>
                          {streams.length > 1 ? (
                            <select className="rd-stream-select" id="rd-stream-select" aria-label="Selecionar stream" defaultValue={defaultStream.url}>
                              {streams.map((s, idx) => (
                                <option key={idx} value={s.url} data-raw-url={s.url}>
                                  {s.title || `Stream ${idx + 1}`}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="rd-stream-label" id="rd-stream-label" data-raw-url={defaultStream.url}>
                              {defaultStream.title || "Stream"}
                            </span>
                          )}
                          <a
                            className="rd-stream-external"
                            id="rd-stream-external"
                            href={defaultStream.url}
                            target="_blank"
                            rel="noopener"
                            aria-label="Abrir stream em nova aba"
                            title="Abrir stream em nova aba"
                          >
                            <i className="fas fa-up-right-from-square" aria-hidden="true"></i>
                          </a>
                        </div>
                      </div>
                    )}

                    <div className="rd-promo-audios" id="rd-promo-audios" data-radio-id={radio.id} hidden>
                      <h3 className="rd-promo-audios-title">Áudios promocionais</h3>
                      <ul className="rd-promo-audios-list" id="rd-promo-audios-list"></ul>
                    </div>

                    <div className="rd-card radio-detail-block radio-detail-report-card rd-report-card">
                      <div className="radio-detail-report-icon">
                        <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                      </div>
                      <div className="radio-detail-report-body">
                        <h3 className="radio-detail-report-title">Não está conseguindo ouvir?</h3>
                        <p className="radio-detail-report-text">
                          Avise a nossa equipe técnica sobre qualquer problema no áudio desta rádio.
                        </p>
                        <button type="button" id="radio-report-open-btn" className="radio-detail-report-btn">
                          <span className="radio-detail-report-btn-icon" aria-hidden="true">
                            <i className="fas fa-comment-alt"></i>
                          </span>
                          <span className="radio-detail-report-btn-label">Relatar problema</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rd-row rd-row-contacts-platforms">
                  {contacts.length > 0 && (
                    <div
                      className="rd-card radio-detail-block radio-detail-slogan-contacts rd-contacts-card"
                      data-rp-section="contacts"
                      data-rp-label="Contatos"
                      data-rp-icon="contact"
                    >
                      <div className="radio-detail-contacts-wrapper">
                        <h2>Contatos</h2>
                        <ul className="radio-detail-contacts radio-detail-contacts-icons">
                          {contacts.map((c, i) => {
                            const { href, target } = contactHref(c);
                            if (!href) return null;
                            const icon = CONTACT_ICONS[c.type || ""] || "fas fa-mobile-alt";
                            return (
                              <li className="radio-detail-contact-item" key={i}>
                                <a
                                  href={href}
                                  {...(target ? { target, rel: "noopener" } : {})}
                                  className="radio-detail-contact-icon radio-detail-contact-icon-only"
                                  aria-label={c.detail || c.title || c.type || "Contato"}
                                >
                                  <i className={icon}></i>
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="rd-card radio-detail-block radio-detail-platforms-block rd-platforms-card">
                    <h2>Plataformas Disponíveis</h2>
                    {platformEntries.length ? (
                      <ul className="radio-detail-platforms-list">
                        {platformEntries.map((p) => {
                          const isImg = p.icon.startsWith("/") || /\.(svg|png|jpe?g|webp)$/i.test(p.icon);
                          const inner = isImg ? (
                            <img src={p.icon} alt={p.label} className="radio-detail-platform-icon-img" />
                          ) : (
                            <i className={p.icon} aria-hidden="true"></i>
                          );
                          const cls =
                            "radio-detail-platform-item" +
                            (p.available ? " is-available" : " is-unavailable") +
                            (!p.available || !p.store_url ? " is-no-link" : "");
                          return (
                            <li className={cls} key={p.key}>
                              {p.available && p.store_url ? (
                                <a
                                  className="radio-detail-platform-link"
                                  href={p.store_url}
                                  target="_blank"
                                  rel="noopener"
                                  aria-label={p.label}
                                >
                                  {inner}
                                </a>
                              ) : (
                                <span className="radio-detail-platform-link" aria-label={p.label}>
                                  {inner}
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="radio-detail-platforms-empty">Em breve</p>
                    )}
                  </div>
                </div>

                <div className="rd-row rd-row-banners rd-row-banners--no-schedule">
                  <div
                    id="radio-banners-carousel"
                    className="rd-banners-carousel"
                    data-radio-id={radio.id}
                    data-rp-section="banners"
                    data-rp-label="Banners"
                    data-rp-icon="image"
                    hidden
                  >
                    <div className="rd-banners-track" id="rd-banners-track"></div>
                    <button
                      type="button"
                      className="rd-banners-arrow rd-banners-arrow--prev"
                      id="rd-banners-prev"
                      aria-label="Banner anterior"
                    >
                      <i className="fas fa-chevron-left" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      className="rd-banners-arrow rd-banners-arrow--next"
                      id="rd-banners-next"
                      aria-label="Próximo banner"
                    >
                      <i className="fas fa-chevron-right" aria-hidden="true"></i>
                    </button>
                    <div className="rd-banners-dots" id="rd-banners-dots" role="tablist" aria-label="Selecionar banner"></div>
                  </div>
                </div>
              </div>

              <section
                id="radio-detail-extra"
                className="radio-detail-extra is-loading"
                data-radio-id={radio.id}
                data-app-lang="pt"
              >
                <div className="radio-detail-extra-overlay" aria-hidden="true">
                  <div className="radio-detail-extra-spinner" aria-hidden="true"></div>
                </div>
                <div id="radio-detail-extra-content" className="radio-detail-extra-content">
                  <div className="radio-detail-block radio-detail-status-block">
                    <p className="radio-detail-status">
                      <small>Carregando programação e conteúdos extras…</small>
                    </p>
                  </div>
                </div>
              </section>

              <section
                className="radio-detail-actions"
                aria-labelledby="radio-detail-actions-heading"
                data-rp-section="actions"
                data-rp-label="Ações"
                data-rp-icon="shield"
                data-station-id={radio.id}
                data-radio-slug={slug}
                data-station-return-path={`/radio/${radio.id}${slug ? "-" + slug : ""}`}
                data-user-logged-in={loggedIn ? "1" : "0"}
              >
                <article className="rd-card radio-detail-block radio-detail-actions-card">
                  <header className="radio-detail-actions-header">
                    <h2 id="radio-detail-actions-heading" className="radio-detail-actions-title">
                      Ações
                    </h2>
                    <p className="radio-detail-actions-lead">Sujeitas a verificação da equipe.</p>
                  </header>
                  <div className="radio-detail-actions-list" role="group" aria-label="Solicitações sobre a rádio">
                    <button
                      type="button"
                      className="radio-detail-actions-btn radio-detail-actions-btn--danger"
                      id="radio-action-delete-btn"
                      data-action="delete_station"
                      aria-label="Solicitar exclusão da rádio"
                    >
                      <span className="radio-detail-actions-btn-icon" aria-hidden="true">
                        <i className="fas fa-trash-alt"></i>
                      </span>
                      <span className="radio-detail-actions-btn-label">Solicitar exclusão da rádio</span>
                    </button>
                    <button
                      type="button"
                      className="radio-detail-actions-btn"
                      id="radio-action-control-btn"
                      data-action="request_control"
                      aria-label="Solicitar controle da rádio"
                    >
                      <span className="radio-detail-actions-btn-icon" aria-hidden="true">
                        <i className="fas fa-sliders-h"></i>
                      </span>
                      <span className="radio-detail-actions-btn-label">Solicitar controle da rádio</span>
                    </button>
                    <button
                      type="button"
                      className="radio-detail-actions-btn"
                      disabled
                      aria-label="Solicitar adição de novos conteúdos na rádio — em breve"
                    >
                      <span className="radio-detail-actions-btn-icon" aria-hidden="true">
                        <i className="fas fa-plus-circle"></i>
                      </span>
                      <span className="radio-detail-actions-btn-label">Solicitar novos conteúdos na rádio</span>
                      <span className="radio-detail-actions-btn-badge">Em breve</span>
                    </button>
                  </div>
                </article>
              </section>
            </>
          )}
        </section>
      </main>

      {radio && (
        <>
          <div className="radio-report-modal" id="radio-report-modal" aria-hidden="true">
            <div className="radio-report-modal-backdrop"></div>
            <div className="radio-report-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="radio-report-modal-title">
              <div className="radio-report-modal-header">
                <div className="radio-report-modal-icon">
                  <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                </div>
                <div className="radio-report-modal-header-text">
                  <h3 id="radio-report-modal-title">Relatar problema na rádio</h3>
                  <p className="radio-report-modal-subtitle">Sua mensagem ajuda nossa equipe a corrigir o stream mais rápido.</p>
                </div>
              </div>
              <form className="radio-report-modal-form" id="radio-report-form" noValidate>
                <div className="radio-report-field">
                  <label htmlFor="radio-report-description">Descreva o problema</label>
                  <textarea
                    id="radio-report-description"
                    name="description"
                    rows={4}
                    maxLength={2000}
                    placeholder="Exemplo: não toca, cai a cada 2 minutos, está sem som..."
                  ></textarea>
                  <p className="radio-report-help">Máximo de 2000 caracteres.</p>
                </div>
                <div className="radio-report-field">
                  <label htmlFor="radio-report-email">E-mail (opcional)</label>
                  <input type="email" id="radio-report-email" name="email" placeholder="seuemail@exemplo.com" />
                  <p className="radio-report-help">Se quiser receber retorno, informe um e-mail válido.</p>
                </div>
                <p className="radio-report-privacy">
                  Usamos essas informações apenas para analisar e tratar o problema relatado.
                </p>
                <div className="radio-report-modal-footer">
                  <button type="button" className="radio-report-btn-secondary" id="radio-report-cancel-btn">
                    Cancelar
                  </button>
                  <button type="submit" className="radio-report-btn-primary" id="radio-report-submit-btn" disabled>
                    Enviar relato
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="radio-report-modal" id="radio-delete-request-modal" aria-hidden="true">
            <div className="radio-report-modal-backdrop"></div>
            <div className="radio-report-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="radio-delete-request-title">
              <div className="radio-report-modal-header">
                <div className="radio-report-modal-icon">
                  <i className="fas fa-trash-alt" aria-hidden="true"></i>
                </div>
                <div className="radio-report-modal-header-text">
                  <h3 id="radio-delete-request-title">Solicitar exclusão da rádio</h3>
                  <p className="radio-report-modal-subtitle">
                    Informe seus dados de contato e o motivo do pedido. A equipe analisará a solicitação.
                  </p>
                </div>
              </div>
              <form className="radio-report-modal-form" id="radio-delete-request-form" noValidate>
                <div className="radio-report-field">
                  <label htmlFor="radio-delete-request-name">Nome</label>
                  <input type="text" id="radio-delete-request-name" name="name" maxLength={120} autoComplete="name" required />
                </div>
                <div className="radio-report-field">
                  <label htmlFor="radio-delete-request-email">E-mail</label>
                  <input type="email" id="radio-delete-request-email" name="email" maxLength={200} autoComplete="email" required />
                </div>
                <div className="radio-report-field">
                  <label htmlFor="radio-delete-request-whatsapp">WhatsApp</label>
                  <input type="tel" id="radio-delete-request-whatsapp" name="whatsapp" maxLength={40} autoComplete="tel" required />
                </div>
                <div className="radio-report-field">
                  <label htmlFor="radio-delete-request-reason">Motivo da exclusão</label>
                  <textarea id="radio-delete-request-reason" name="reason" rows={4} maxLength={2000} required></textarea>
                </div>
                <p className="radio-report-feedback" id="radio-delete-request-feedback" role="status" aria-live="polite" hidden></p>
                <div className="radio-report-modal-footer">
                  <button type="button" className="radio-report-btn-secondary" id="radio-delete-request-cancel-btn">
                    Cancelar
                  </button>
                  <button type="submit" className="radio-report-btn-primary" id="radio-delete-request-submit-btn">
                    Enviar solicitação
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="radio-report-modal" id="radio-control-request-modal" aria-hidden="true">
            <div className="radio-report-modal-backdrop"></div>
            <div className="radio-report-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="radio-control-request-title">
              <div className="radio-report-modal-header">
                <div className="radio-report-modal-icon">
                  <i className="fas fa-sliders-h" aria-hidden="true"></i>
                </div>
                <div className="radio-report-modal-header-text">
                  <h3 id="radio-control-request-title">Solicitar controle da rádio</h3>
                  <p className="radio-report-modal-subtitle">
                    Informe e-mail e WhatsApp para que possamos validar o pedido de controle.
                  </p>
                </div>
              </div>
              <form className="radio-report-modal-form" id="radio-control-request-form" noValidate>
                <div className="radio-report-field">
                  <label htmlFor="radio-control-request-email">E-mail</label>
                  <input type="email" id="radio-control-request-email" name="email" maxLength={200} autoComplete="email" required />
                </div>
                <div className="radio-report-field">
                  <label htmlFor="radio-control-request-whatsapp">WhatsApp</label>
                  <input type="tel" id="radio-control-request-whatsapp" name="whatsapp" maxLength={40} autoComplete="tel" required />
                </div>
                <p className="radio-report-feedback" id="radio-control-request-feedback" role="status" aria-live="polite" hidden></p>
                <div className="radio-report-modal-footer">
                  <button type="button" className="radio-report-btn-secondary" id="radio-control-request-cancel-btn">
                    Cancelar
                  </button>
                  <button type="submit" className="radio-report-btn-primary" id="radio-control-request-submit-btn">
                    Enviar solicitação
                  </button>
                </div>
              </form>
            </div>
          </div>

          {defaultStream && (
            <div
              className="radio-global-player"
              id="radio-global-player"
              aria-hidden="true"
              data-radio-id={radio.id}
              data-radio-title={radio.title || ""}
              data-program-text={programTitle}
            >
              <div className="radio-global-player-inner">
                <div className="radio-global-player-overlay" aria-hidden="true">
                  <div className="radio-global-player-spinner" aria-hidden="true"></div>
                </div>
                <div className="radio-global-player-content">
                  <div className="radio-global-player-info">
                    <div className="radio-global-player-logo">
                      <img
                        src={logo}
                        alt={radio.title || "Rádio"}
                        decoding="async"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src = "/assets/img/header/logo.png";
                        }}
                      />
                    </div>
                    <div className="radio-global-player-text">
                      <p className="radio-global-player-title">{radio.title}</p>
                      <p className="radio-global-player-subtitle">{radio.segmento || "Ao vivo"}</p>
                    </div>
                  </div>
                  <div className="radio-global-player-program">
                    <div className="radio-global-player-program-track"></div>
                  </div>
                  <div className="radio-global-player-right">
                    <button
                      type="button"
                      className="radio-global-favorite-btn"
                      data-radio-id={radio.id}
                      aria-label="Adicionar aos favoritos"
                    >
                      <span className="radio-global-favorite-btn__icons" aria-hidden="true">
                        <i className="far fa-heart favorite-icon-empty"></i>
                        <i className="fas fa-heart favorite-icon-filled"></i>
                      </span>
                      <span className="radio-global-favorite-btn__spinner-wrap" aria-hidden="true">
                        <span className="radio-favorite-btn-spinner radio-favorite-btn-spinner--global"></span>
                      </span>
                    </button>
                    <button type="button" className="radio-global-player-play" aria-label="Pausar">
                      <i className="fas fa-pause" aria-hidden="true"></i>
                    </button>
                    <div className="radio-global-player-volume">
                      <span className="radio-global-player-volume-icon" aria-hidden="true">
                        <i className="fas fa-volume-up"></i>
                      </span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        defaultValue={70}
                        className="radio-global-player-volume-range"
                        aria-label="Volume"
                      />
                      <span className="radio-global-player-volume-percent">70%</span>
                    </div>
                    <button type="button" className="radio-global-player-close" aria-label="Fechar player">
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <Footer />
      <ProtonWidget />
    </>
  );
}
